/**
 * SmartLink targeting hub — shared data-layer client.
 *
 * Single module that speaks to the eight SmartLink targeting APIs behind one
 * tabbed page:
 *
 *   - /api/v1/smartlink-geo         (country allow/deny)
 *   - /api/v1/smartlink-os          (os allow/deny)
 *   - /api/v1/smartlink-device      (phone|tablet|desktop|tv)
 *   - /api/v1/smartlink-carrier     (iso carrier codes)
 *   - /api/v1/smartlink-cookieless  (on/off toggle)
 *   - /api/v1/smartlink-mode        (manual|auto)
 *   - /api/v1/smartlink-split       (bps rules)
 *   - /api/v1/smartlink-gate        (plan-entitlement gate status)
 *
 * Intentionally thin: every endpoint exposes the same CRUD surface keyed by
 * `smartlink_id`. The page drives the tab; this file owns the transport.
 *
 * No React, no TanStack — the UI layer imports these and wraps with
 * useQuery / useMutation locally. Keeps the file testable from bun/node
 * harnesses with plain fetch stubbing.
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Tab catalog — the single source of truth for the page's tab order, slugs,
// labels, and endpoint base paths. The component iterates this; tests assert
// against it; URL param `?tab=<slug>` maps to `slug` here.
// ---------------------------------------------------------------------------

export const TARGETING_TABS = [
	{
		slug: "geo",
		label: "Geo",
		endpoint: "/smartlink-geo",
		blurb: "Allow or deny traffic by ISO country code.",
	},
	{
		slug: "os",
		label: "OS",
		endpoint: "/smartlink-os",
		blurb: "Constrain by operating system family.",
	},
	{
		slug: "device",
		label: "Device",
		endpoint: "/smartlink-device",
		blurb: "Route phone, tablet, desktop, or TV traffic.",
	},
	{
		slug: "carrier",
		label: "Carrier",
		endpoint: "/smartlink-carrier",
		blurb: "Filter by mobile carrier codes.",
	},
	{
		slug: "cookieless",
		label: "Cookieless",
		endpoint: "/smartlink-cookieless",
		blurb: "Toggle cookieless attribution mode.",
	},
	{
		slug: "mode",
		label: "Mode",
		endpoint: "/smartlink-mode",
		blurb: "Pick manual or auto rotation mode.",
	},
	{
		slug: "split",
		label: "Split",
		endpoint: "/smartlink-split",
		blurb: "Assign basis-point split rules across offers.",
	},
	{
		slug: "gate",
		label: "Gate",
		endpoint: "/smartlink-gate",
		blurb: "Inspect the plan-entitlement gate for this SmartLink.",
	},
] as const;

export type TargetingTabSlug = (typeof TARGETING_TABS)[number]["slug"];

export const TARGETING_TAB_SLUGS = TARGETING_TABS.map((t) => t.slug) as readonly TargetingTabSlug[];

export function isTargetingTabSlug(value: unknown): value is TargetingTabSlug {
	return typeof value === "string" && TARGETING_TAB_SLUGS.includes(value as TargetingTabSlug);
}

export function resolveTabFromSearch(raw: string | undefined | null): TargetingTabSlug {
	if (typeof raw === "string" && isTargetingTabSlug(raw)) return raw;
	return TARGETING_TABS[0].slug;
}

export function findTab(slug: TargetingTabSlug) {
	return TARGETING_TABS.find((t) => t.slug === slug) ?? TARGETING_TABS[0];
}

// ---------------------------------------------------------------------------
// Shared row shape — every targeting endpoint returns a tenant-scoped object
// keyed by `smartlink_id`. The payload column differs per tab; we use a
// generic `config` field so the hub renders a single summary table.
// ---------------------------------------------------------------------------

export interface TargetingRow<TConfig = unknown> {
	id: string;
	tenant_id: string;
	smartlink_id: string;
	config: TConfig;
	updated_at: number;
}

export interface TargetingListResponse<TConfig = unknown> {
	rows: TargetingRow<TConfig>[];
}

export interface TargetingSingleResponse<TConfig = unknown> {
	row: TargetingRow<TConfig>;
}

// ---------------------------------------------------------------------------
// Per-tab payload shapes — kept flat so the minimal forms can bind directly.
// ---------------------------------------------------------------------------

export type GeoMode = "allow" | "deny";

export interface GeoConfig {
	mode: GeoMode;
	countries: string[];
}

export interface OsConfig {
	mode: GeoMode;
	os_list: string[];
}

export type DeviceClass = "phone" | "tablet" | "desktop" | "tv";

export interface DeviceConfig {
	mode: GeoMode;
	devices: DeviceClass[];
}

export interface CarrierConfig {
	mode: GeoMode;
	carriers: string[];
}

export interface CookielessConfig {
	enabled: boolean;
}

export type SmartlinkMode = "manual" | "auto";

export interface ModeConfig {
	mode: SmartlinkMode;
}

export interface SplitRule {
	offer_id: string;
	bps: number;
}

export interface SplitConfig {
	rules: SplitRule[];
}

export interface GateConfig {
	plan: string;
	entitled: boolean;
	reason: string | null;
}

// Discriminated union of all configs — useful for the summary list.
export type AnyTargetingConfig =
	| GeoConfig
	| OsConfig
	| DeviceConfig
	| CarrierConfig
	| CookielessConfig
	| ModeConfig
	| SplitConfig
	| GateConfig;

// ---------------------------------------------------------------------------
// Validation — hand-rolled `safeParse` mirrors per tab. Zero dep on zod.
// ---------------------------------------------------------------------------

export interface ParseOk<T> {
	success: true;
	data: T;
}
export interface ParseErr {
	success: false;
	errors: Record<string, string>;
}
export type ParseResult<T> = ParseOk<T> | ParseErr;

const SMARTLINK_ID_MAX = 120;

export function parseSmartlinkId(value: string | undefined | null): ParseResult<string> {
	const trimmed = (value ?? "").trim();
	if (trimmed.length === 0) {
		return { success: false, errors: { smartlinkId: "SmartLink ID is required." } };
	}
	if (trimmed.length > SMARTLINK_ID_MAX) {
		return {
			success: false,
			errors: { smartlinkId: `SmartLink ID must be ${SMARTLINK_ID_MAX} chars or fewer.` },
		};
	}
	if (/[\x00-\x1f]/.test(trimmed)) {
		return {
			success: false,
			errors: { smartlinkId: "SmartLink ID contains invalid control characters." },
		};
	}
	return { success: true, data: trimmed };
}

export function parseCsvCodes(value: string, max = 64): ParseResult<string[]> {
	const codes = value
		.split(/[\s,]+/)
		.map((c) => c.trim().toUpperCase())
		.filter((c) => c.length > 0);
	if (codes.length === 0) {
		return { success: false, errors: { codes: "Enter at least one code." } };
	}
	if (codes.length > max) {
		return { success: false, errors: { codes: `At most ${max} codes.` } };
	}
	for (const code of codes) {
		if (!/^[A-Z0-9_-]{1,16}$/.test(code)) {
			return {
				success: false,
				errors: { codes: `Invalid code: ${code}. Use letters, numbers, - or _.` },
			};
		}
	}
	return { success: true, data: Array.from(new Set(codes)) };
}

export function parseDeviceList(selected: readonly string[]): ParseResult<DeviceClass[]> {
	const allowed = new Set<DeviceClass>(["phone", "tablet", "desktop", "tv"]);
	const cleaned: DeviceClass[] = [];
	for (const raw of selected) {
		if (!allowed.has(raw as DeviceClass)) {
			return { success: false, errors: { devices: `Unsupported device: ${raw}.` } };
		}
		if (!cleaned.includes(raw as DeviceClass)) cleaned.push(raw as DeviceClass);
	}
	if (cleaned.length === 0) {
		return { success: false, errors: { devices: "Pick at least one device class." } };
	}
	return { success: true, data: cleaned };
}

export function parseSplitRules(
	rows: readonly { offer_id: string; bps: number | string }[],
): ParseResult<SplitRule[]> {
	const rules: SplitRule[] = [];
	let total = 0;
	for (let i = 0; i < rows.length; i += 1) {
		const row = rows[i];
		const offerId = row.offer_id.trim();
		if (offerId.length === 0) {
			return { success: false, errors: { [`rules.${i}`]: "Offer ID required." } };
		}
		const bps = typeof row.bps === "number" ? row.bps : Number.parseInt(row.bps, 10);
		if (!Number.isFinite(bps) || bps < 0 || bps > 10000) {
			return { success: false, errors: { [`rules.${i}`]: "Bps must be 0–10000." } };
		}
		total += bps;
		rules.push({ offer_id: offerId, bps });
	}
	if (rules.length === 0) {
		return { success: false, errors: { rules: "Add at least one split rule." } };
	}
	if (total !== 10000) {
		return { success: false, errors: { rules: `Total must equal 10000 bps (got ${total}).` } };
	}
	return { success: true, data: rules };
}

// ---------------------------------------------------------------------------
// Generic remote ops — every tab has the same read-list + read-one + upsert
// shape. Individual forms pre-shape their config, then call `saveTargeting`.
// ---------------------------------------------------------------------------

export interface FetchListArgs {
	endpoint: string;
	smartlinkId: string | null;
}

/** List rows for a tab. When `smartlinkId` is set, scopes the server query. */
export async function fetchTargetingList<TConfig = unknown>(
	args: FetchListArgs,
): Promise<TargetingRow<TConfig>[]> {
	const query = args.smartlinkId ? { smartlink_id: args.smartlinkId } : undefined;
	try {
		const res = await apiClient.get<TargetingListResponse<TConfig>>(args.endpoint, {
			query,
		});
		return Array.isArray(res.rows) ? res.rows : [];
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return [];
		throw err;
	}
}

export interface FetchOneArgs {
	endpoint: string;
	smartlinkId: string;
}

/** Fetch the single row for a SmartLink, or null if none exists yet. */
export async function fetchTargetingOne<TConfig = unknown>(
	args: FetchOneArgs,
): Promise<TargetingRow<TConfig> | null> {
	try {
		const res = await apiClient.get<TargetingSingleResponse<TConfig>>(
			`${args.endpoint}/${encodeURIComponent(args.smartlinkId)}`,
		);
		return res.row;
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return null;
		throw err;
	}
}

export interface SaveTargetingArgs<TConfig> {
	endpoint: string;
	smartlinkId: string;
	config: TConfig;
}

/** Upsert via PUT — every backend route supports create-or-replace semantics. */
export async function saveTargeting<TConfig>(
	args: SaveTargetingArgs<TConfig>,
): Promise<TargetingRow<TConfig>> {
	const res = await apiClient.put<TargetingSingleResponse<TConfig>>(
		`${args.endpoint}/${encodeURIComponent(args.smartlinkId)}`,
		args.config,
	);
	return res.row;
}

/** Create via POST — used by tabs that prefer explicit creation semantics. */
export async function createTargeting<TConfig>(
	args: SaveTargetingArgs<TConfig>,
): Promise<TargetingRow<TConfig>> {
	const res = await apiClient.post<TargetingSingleResponse<TConfig>>(args.endpoint, {
		smartlink_id: args.smartlinkId,
		...(args.config as Record<string, unknown>),
	});
	return res.row;
}

// ---------------------------------------------------------------------------
// Query keys — namespaced per tab so invalidations stay surgical.
// ---------------------------------------------------------------------------

export const smartlinksTargetingQueryKeys = {
	all: ["smartlinks-targeting"] as const,
	tabList: (slug: TargetingTabSlug, smartlinkId: string | null) =>
		[...smartlinksTargetingQueryKeys.all, slug, "list", smartlinkId ?? ""] as const,
	tabOne: (slug: TargetingTabSlug, smartlinkId: string) =>
		[...smartlinksTargetingQueryKeys.all, slug, "one", smartlinkId] as const,
};

// ---------------------------------------------------------------------------
// Default configs per tab — used to hydrate an empty form before the user
// types. Exposed so tests can assert shape parity without mocking the API.
// ---------------------------------------------------------------------------

export function defaultConfigFor(slug: TargetingTabSlug): AnyTargetingConfig {
	switch (slug) {
		case "geo":
			return { mode: "allow", countries: [] } satisfies GeoConfig;
		case "os":
			return { mode: "allow", os_list: [] } satisfies OsConfig;
		case "device":
			return { mode: "allow", devices: [] } satisfies DeviceConfig;
		case "carrier":
			return { mode: "allow", carriers: [] } satisfies CarrierConfig;
		case "cookieless":
			return { enabled: false } satisfies CookielessConfig;
		case "mode":
			return { mode: "manual" } satisfies ModeConfig;
		case "split":
			return { rules: [] } satisfies SplitConfig;
		case "gate":
			return { plan: "free", entitled: false, reason: null } satisfies GateConfig;
	}
}
